import { useEffect, useRef, useState } from 'react';
import { buildMessage, createClient, createJsonSerdes, createMemoryAdapter, createWebRTCAdapter, } from '@varos/openai-realtime-client';
export function createClientController(config) {
    const { autoReplyIfEmpty, autosaveIntervalMs: autosaveDuration = 500, // Default value
    maxMessageBufferSize, } = config;
    let nextId = 0;
    return {
        useProps(props) {
            const { connection: { dataChannel }, } = props;
            const [client, setClient] = useState(null);
            const [messages, setMessages] = useState(props.initial);
            const messagesRef = useRef(props.initial);
            // State for user speaking status
            const [userSpeakingState, setUserSpeakingState] = useState('idle');
            // Callback refs
            const onMessageRef = useRef(props.onMessage);
            const onUpdateRef = useRef(props.onUpdate);
            const onAutosaveRef = useRef(props.onAutosave);
            useEffect(() => {
                onMessageRef.current = props.onMessage;
            }, [props.onMessage]);
            useEffect(() => {
                onUpdateRef.current = props.onUpdate;
            }, [props.onUpdate]);
            useEffect(() => {
                onAutosaveRef.current = props.onAutosave;
            }, [props.onAutosave]);
            // [NEW] Internal ref to track the isEnding status, synced with the prop
            const internalIsEndingRef = useRef(props.disabled ?? false);
            useEffect(() => {
                internalIsEndingRef.current = props.disabled ?? false;
            }, [props.disabled]);
            const pendingActionsRef = useRef([]);
            const hasSubmittedInitialResponseRef = useRef(false);
            const sessionReadyRef = useRef(false);
            // [ADD] Idle timer for autosave
            const idleTimeoutRef = useRef(null);
            const lastSavedMessagesRef = useRef(new Set());
            /**
             * [ADD] Helper to trim the oldest messages if we exceed maxMessageBufferSize.
             * Because we prepend new messages at the front, the oldest are at the end.
             */
            function trimMessagesIfNeeded(list) {
                if (maxMessageBufferSize && list.length > maxMessageBufferSize) {
                    // keep the first maxMessageBufferSize items (drop oldest at the end)
                    return list.slice(0, maxMessageBufferSize);
                }
                return list;
            }
            /**
             * [ADD] Helper to add a new message at the front, then trim if needed.
             * We apply the same logic to both the state array and the ref array.
             */
            function addNewMessage(newMsg) {
                setMessages((prev) => {
                    let updated = [newMsg, ...prev];
                    updated = trimMessagesIfNeeded(updated);
                    return updated;
                });
                let updatedRef = [newMsg, ...messagesRef.current];
                updatedRef = trimMessagesIfNeeded(updatedRef);
                messagesRef.current = updatedRef;
            }
            function startIdleTimer() {
                if (idleTimeoutRef.current) {
                    clearTimeout(idleTimeoutRef.current);
                }
                idleTimeoutRef.current = setTimeout(async () => {
                    const autosaveCallback = onAutosaveRef.current;
                    if (!autosaveCallback)
                        return;
                    // find any messages that haven't been saved
                    const messagesToSave = messagesRef.current.filter((msg) => {
                        const isInitial = props.initial.some((init) => init.id === msg.id);
                        const isAlreadySaved = lastSavedMessagesRef.current.has(msg.id);
                        return !isInitial && !isAlreadySaved;
                    });
                    if (messagesToSave.length > 0) {
                        messagesToSave.forEach((m) => lastSavedMessagesRef.current.add(m.id));
                        await autosaveCallback(messagesToSave.slice().reverse());
                    }
                }, autosaveDuration);
            }
            function updateMessageStatus(messageId, newStatus) {
                setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, localStatus: newStatus } : m));
                messagesRef.current = messagesRef.current.map((m) => m.id === messageId ? { ...m, localStatus: newStatus } : m);
            }
            // Create the client once we have a dataChannel
            useEffect(() => {
                if (!dataChannel) {
                    setClient(null);
                    sessionReadyRef.current = false; // Reset session ready state
                    hasSubmittedInitialResponseRef.current = false; // Reset initial response state
                    return;
                }
                // Reset session ready state when creating new client
                sessionReadyRef.current = false;
                hasSubmittedInitialResponseRef.current = false; // Reset initial response state
                const instance = createClient({
                    adapter: {
                        serdes: createJsonSerdes(),
                        connection: createWebRTCAdapter({ channel: dataChannel }),
                        message: createMemoryAdapter({ initial: [] }),
                    },
                    tools: [...props.tools],
                });
                setClient(instance);
                instance.start({});
                return () => {
                    instance.stop();
                    setClient(null);
                    sessionReadyRef.current = false; // Reset session ready state
                    hasSubmittedInitialResponseRef.current = false; // Reset initial response state
                };
            }, [dataChannel?.label]);
            // Update the client if tools change and dataChannel is open
            useEffect(() => {
                if (!client)
                    return;
                if (dataChannel?.readyState === 'open') {
                    client.update({ tools: props.tools });
                }
            }, [client, dataChannel?.readyState, props.tools]);
            // Inbound message handler
            useEffect(() => {
                if (!client)
                    return;
                const msgHandler = (message) => {
                    // [MODIFIED] Stop processing if ending (check internal ref)
                    if (internalIsEndingRef.current) {
                        console.log(`[client] isEnding=${internalIsEndingRef.current}, ignoring incoming message:`, message.id);
                        return;
                    }
                    // skip if we already have this message
                    if (messagesRef.current.find((m) => m.id === message.id)) {
                        return;
                    }
                    const newMsg = {
                        ...message,
                        localStatus: 'sent',
                    };
                    addNewMessage(newMsg);
                    onMessageRef.current?.(newMsg);
                    startIdleTimer();
                };
                client.on('message', msgHandler);
                return () => {
                    client.off('message', msgHandler);
                };
            }, [client]);
            // Inbound update handler - particularly important for voice transcription updates
            useEffect(() => {
                if (!client)
                    return;
                const updateHandler = (updated) => {
                    // [MODIFIED] Stop processing if ending (check internal ref)
                    if (internalIsEndingRef.current) {
                        console.log(`[client] isEnding=${internalIsEndingRef.current}, ignoring incoming update:`, updated.id);
                        return;
                    }
                    setMessages((prev) => {
                        return prev.map((m) => {
                            if (m.id !== updated.id)
                                return m;
                            // For audio messages, ensure we update the transcript
                            if (m.kind === 'audio' && updated.kind === 'audio') {
                                return {
                                    ...updated,
                                    transcript: updated.transcript || m.transcript,
                                    localStatus: m.localStatus === 'failed' ? 'failed' : 'sent',
                                };
                            }
                            return {
                                ...updated,
                                localStatus: m.localStatus === 'failed' ? 'failed' : 'sent',
                            };
                        });
                    });
                    messagesRef.current = messagesRef.current.map((m) => {
                        if (m.id !== updated.id)
                            return m;
                        // For audio messages, ensure we update the transcript in the ref as well
                        if (m.kind === 'audio' && updated.kind === 'audio') {
                            return {
                                ...updated,
                                transcript: updated.transcript || m.transcript,
                                localStatus: m.localStatus === 'failed' ? 'failed' : 'sent',
                            };
                        }
                        return {
                            ...updated,
                            localStatus: m.localStatus === 'failed' ? 'failed' : 'sent',
                        };
                    });
                    // Make sure to call onUpdate with the updated message
                    onUpdateRef.current?.(updated);
                    startIdleTimer();
                };
                client.on('update', updateHandler);
                return () => {
                    client.off('update', updateHandler);
                };
            }, [client]);
            // Effect to handle speech start/stop and duration
            useEffect(() => {
                if (!client)
                    return;
                const handleSpeechStarted = (data) => {
                    if (data.speaker === 'user') {
                        console.log('[client] User speech started at:', data.timestamp);
                        setUserSpeakingState('speaking');
                    }
                };
                const handleSpeechStopped = (data) => {
                    if (data.speaker === 'user') {
                        console.log('[client] User speech stopped at:', data.timestamp);
                        setUserSpeakingState('idle');
                    }
                };
                client.on('speech_started', handleSpeechStarted);
                client.on('speech_stopped', handleSpeechStopped);
                return () => {
                    client.off('speech_started', handleSpeechStarted);
                    client.off('speech_stopped', handleSpeechStopped);
                };
            }, [client]);
            // Flush pending actions when dataChannel becomes open
            useEffect(() => {
                if (!client)
                    return;
                if (dataChannel?.readyState === 'open') {
                    while (pendingActionsRef.current.length > 0) {
                        const action = pendingActionsRef.current.shift();
                        if (!action)
                            break;
                        if (action.type === 'send') {
                            client
                                .send(action.message)
                                .then((res) => {
                                updateMessageStatus(action.message.id, 'sent');
                                action.resolve(res);
                            })
                                .catch((err) => {
                                updateMessageStatus(action.message.id, 'failed');
                                action.reject(err);
                            });
                        }
                        else if (action.type === 'reply') {
                            client.reply().then(action.resolve).catch(action.reject);
                        }
                        else if (action.type === 'directive') {
                            client
                                .sendDirective({ role: action.role, text: action.text })
                                .then(action.resolve)
                                .catch(action.reject);
                        }
                    }
                }
            }, [client, dataChannel?.readyState]);
            // Listen for session_ready event and handle auto-reply directly
            useEffect(() => {
                if (!client)
                    return;
                const sessionReadyHandler = () => {
                    console.log('Session is ready - tools and configuration have been set');
                    sessionReadyRef.current = true;
                    // Auto-creation of initial assistant reply directly when session is ready
                    if (autoReplyIfEmpty &&
                        !hasSubmittedInitialResponseRef.current &&
                        dataChannel?.readyState === 'open') {
                        if (messagesRef.current.length === 0) {
                            hasSubmittedInitialResponseRef.current = true;
                            console.log('auto-creating initial response - session is ready');
                            // Send a system directive to trigger the initial response
                            client
                                .sendDirective({
                                role: 'system',
                                text: 'The conversation is starting. Please provide an appropriate greeting and introduction to begin the interview.',
                            })
                                .then(() => {
                                console.log('initial system directive sent successfully');
                            })
                                .catch((err) => {
                                console.error('initial system directive error', err);
                            });
                        }
                    }
                };
                client.on('session_ready', sessionReadyHandler);
                return () => {
                    client.off('session_ready', sessionReadyHandler);
                };
            }, [client, autoReplyIfEmpty, dataChannel?.readyState]);
            // Send a user message
            async function send(payload) {
                return new Promise((resolve, reject) => {
                    const newMsg = buildMessage({
                        id: (nextId++).toString(),
                        ...payload,
                    });
                    newMsg.localStatus = 'pending';
                    // Add the message optimistically
                    addNewMessage(newMsg);
                    if (!client || dataChannel?.readyState !== 'open') {
                        pendingActionsRef.current.push({
                            type: 'send',
                            message: newMsg,
                            resolve,
                            reject,
                        });
                    }
                    else {
                        client
                            .send(newMsg)
                            .then((res) => {
                            updateMessageStatus(newMsg.id, 'sent');
                            resolve(res);
                        })
                            .catch((err) => {
                            updateMessageStatus(newMsg.id, 'failed');
                            reject(err);
                        });
                    }
                });
            }
            // Send a directive to the model without adding a message to the conversation
            async function sendDirective(props) {
                console.log('Sending directive:', props.text);
                if (!client || dataChannel?.readyState !== 'open') {
                    console.log('Adding to pending actions queue - client not ready');
                    return new Promise((resolve, reject) => {
                        pendingActionsRef.current.push({
                            type: 'directive',
                            text: props.text,
                            role: props.role,
                            resolve,
                            reject,
                        });
                    });
                }
                else {
                    console.log('Sending directive immediately');
                    return client.sendDirective(props);
                }
            }
            // Trigger an assistant reply
            async function reply() {
                return new Promise((resolve, reject) => {
                    if (!client || dataChannel?.readyState !== 'open') {
                        pendingActionsRef.current.push({
                            type: 'reply',
                            resolve,
                            reject,
                        });
                    }
                    else {
                        client.reply().then(resolve).catch(reject);
                    }
                });
            }
            return {
                client,
                message: {
                    items: messages,
                },
                send,
                sendDirective,
                reply,
                speakingStatus: userSpeakingState === 'speaking' ? 'user' : null,
                speechStartTime: null,
                userSpeakingState,
            };
        },
    };
}
//# sourceMappingURL=clientController.js.map